import { useEffect } from "react";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useBoundaryService } from "../../hooks";
import BoundaryList from "./BoundaryList";
import BoundaryMap from "../map/BoundaryMap";
import BoundarySettingsModal from "./BoundarySettingsModal";
import UploadBoundaryModal from "./UploadBoundaryModal";
import SearchToolbar from "./SearchToolbar";
import { useBoundarySelectContext } from "./BoundarySelectContext";
import { BoundarySource } from "../../constant/BoundarySource";
import { MultiPolygon, Polygon } from "ol/geom";
import { LocalStorageKeys } from "../../types/LocalStorageKeys";
import { StoredBoundaryState } from "../../types/Boundary/StoredBoundaryState";

const MapContainer = styled.div`
  position: relative;
  flex: 1;
`;

const getBoundaries = () => {
  const storedBoundaryState = localStorage.getItem(LocalStorageKeys.BOUNDARIES);
  if (storedBoundaryState == null) {
    return [];
  }
  const boundaries = JSON.parse(storedBoundaryState) as StoredBoundaryState;
  const parsedBoundaries = boundaries.boundaries.map((b) => ({
    ...b,
    geometry:
      b.source === BoundarySource.DrawnBoundary ?
        new Polygon(b.geometry)
      : new MultiPolygon(b.geometry),
  }));

  if (DateTime.fromISO(boundaries.time).plus({ hours: 1 }) > DateTime.local()) {
    return parsedBoundaries;
  } else {
    localStorage.removeItem(LocalStorageKeys.BOUNDARIES);
    return [];
  }
};

const BoundarySelection = () => {
  const { boundary: boundaryParam } = useParams();
  const navigate = useNavigate();
  const { addBoundary, addBoundaries, clearBoundaries } =
    useBoundarySelectContext();
  const boundaryService = useBoundaryService();

  useEffect(() => {
    if (boundaryParam) {
      clearBoundaries();
      boundaryService
        .listAll()
        .then((boundaries) => {
          const boundary = boundaries.find(
            (b) => b.name.toLowerCase() === boundaryParam.toLowerCase(),
          );
          if (boundary) {
            boundaryService
              .getById(boundary.boundaryId)
              .then((boundary) => {
                addBoundary({
                  ...boundary,
                  geometry: new MultiPolygon(boundary.geometry.coordinates),
                  source: BoundarySource.KnownBoundary,
                });
              })
              .catch(() => {})
              .finally(() => {});
          } else {
            navigate("/");
          }
        })
        .catch(() => {});
    } else {
      const storedBoundaries = getBoundaries();
      addBoundaries(storedBoundaries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryParam]);

  return (
    <MapContainer>
      <SearchToolbar />
      <BoundaryMap />
      <BoundaryList />
      <BoundarySettingsModal />
      <UploadBoundaryModal />
    </MapContainer>
  );
};

export default BoundarySelection;
